import React, { useEffect, useState, useMemo } from "react";
import FarmerLayout from "../../components/layouts/FarmerLayout";
// eslint-disable-next-line no-unused-vars
import { getDatabase, ref as dbRef, onValue, off, get as dbGet } from "firebase/database";
import { auth, firestore, database } from "../../firebaseConfig"; // adjust path if needed
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import toast from "react-hot-toast";

/**
 * FarmerMonthlyRecords
 *
 * Props:
 *  - companyName (optional)
 *  - farmerOtherName (optional)  // corresponds to OtherName in your schema
 *
 * Behavior:
 *  - If companyName or farmerOtherName not provided, attempt to resolve from logged-in user's Firestore profile
 *    (users/{uid}.companyName and users/{uid}.otherName).
 *
 * Firebase path used:
 *  Companies/{companyName}/FarmerRecords/{farmerOtherName}/MilkRecords/{year}/{month}/{day}
 */

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];

const DayCell = ({ day, value }) => (
  <div className="p-2 border rounded bg-white shadow-sm text-sm">
    <div className="text-xs text-gray-400 mb-1">Day {day}</div>
    <div className="font-semibold text-cyan-700">
      {value !== undefined && value !== "" ? `${Number(value).toFixed(1)} L` : "—"}
    </div>
  </div>
);

const FarmerMonthlyRecords = ({ companyName: propCompanyName, farmerOtherName: propFarmerOtherName , farmerFullName: propFarmerFullName }) => {
  const [companyName, setCompanyName] = useState(propCompanyName || "");
  const [farmerOtherName, setFarmerOtherName] = useState(propFarmerOtherName || "");
  const [farmerFullName, setFarmerFullName] = useState(propFarmerFullName || ""); // optional, not used in path
  const [loading, setLoading] = useState(true);

  const today = new Date();
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [selectedMonthIndex, setSelectedMonthIndex] = useState(today.getMonth()); // 0-11

  const [dailyRecords, setDailyRecords] = useState({}); // { "1": "12.3", "2": "..." }
  // eslint-disable-next-line no-unused-vars
  const [realtimeListenerRef, setRealtimeListenerRef] = useState(null);
  const db = database; // use exported instance

  // Resolve missing props using auth -> users/{uid}
  useEffect(() => {
    let unsubAuth = null;
    if (propCompanyName && propFarmerOtherName) {
      // props provided — nothing to resolve
      return;
    }

    unsubAuth = onAuthStateChanged(auth, async (user) => {
      if (!user) return;
      try {
        const userRef = doc(firestore, "users", user.uid);
        const snap = await getDoc(userRef);
        if (!snap.exists()) return;
        const data = snap.data();
        if (!propCompanyName && data.companyName) setCompanyName(data.companyName);
        if (!propFarmerOtherName && data.otherName) setFarmerOtherName(data.otherName);
        if (!propFarmerFullName && data.fullName) setFarmerFullName(data.fullName);
      } catch (err) {
        console.error("Failed to resolve user data:", err);
      }
    });

    return () => {
      if (unsubAuth) unsubAuth();
    };
    // Only run on mount — don't include db/firestore in deps to avoid re-trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build firebase path for the selected month
  const monthPath = useMemo(() => {
    if (!companyName || !farmerOtherName) return null;
    const year = String(selectedYear);
    const month = String(selectedMonthIndex + 1); // use 1-based month in DB
    return `Companies/${companyName}/FarmerRecords/${farmerOtherName}/${year}/${month}`;
  }, [companyName, farmerOtherName, selectedYear, selectedMonthIndex]);

  // Subscribe to realtime updates for the selected month
  useEffect(() => {
    if (!monthPath || !db) {
      setDailyRecords({});
      setLoading(false);
      return;
    }

    setLoading(true);
    const ref = dbRef(db, monthPath);

    const handleSnapshot = (snap) => {
      const val = snap.val() || {};
      // val is expected to be { "1": "12.3", "2": "..." }
      setDailyRecords(val);
      setLoading(false);
    };

    onValue(ref, handleSnapshot, (err) => {
      console.error("Realtime error:", err);
      toast.error("Error loading milk records");
      setLoading(false);
    });

    // keep ref so we can call off on path change/unmount
    setRealtimeListenerRef(ref);

    return () => {
      if (ref) off(ref);
      setRealtimeListenerRef(null);
    };
  }, [monthPath, db]);

  // Utilities: totals
  const totals = useMemo(() => {
    let firstHalf = 0;
    let secondHalf = 0;
    let monthly = 0;

    for (let d = 1; d <= 31; d++) {
      const key = String(d);
      const raw = dailyRecords[key];
      const num = parseFloat(raw);
      if (!isNaN(num)) {
        monthly += num;
        if (d <= 15) firstHalf += num;
        else secondHalf += num;
      }
    }
    return { firstHalf, secondHalf, monthly };
  }, [dailyRecords]);

  // compute current week total for the selected month/year
  const currentWeekTotal = useMemo(() => {
    // find current date if viewing current month, else compute week of 1st... choose week that contains today only if same month/year
    const now = new Date();
    const viewYear = selectedYear;
    const viewMonth = selectedMonthIndex; // 0-based

    // compute the week containing today only if we are viewing the month containing today
    if (now.getFullYear() === viewYear && now.getMonth() === viewMonth) {
      // compute week start (Sunday) and sum 7 days from sunday to saturday containing today
      const todayDay = now.getDate();
      // find local day-of-week: 0 (Sun) .. 6 (Sat)
      const dow = now.getDay();
      const sundayDate = todayDay - dow; // may be <=0 (previous month) but those days won't exist in dailyRecords
      let total = 0;
      for (let offset = 0; offset < 7; offset++) {
        const d = sundayDate + offset;
        if (d >= 1 && d <= 31) {
          const raw = dailyRecords[String(d)];
          const num = parseFloat(raw);
          if (!isNaN(num)) total += num;
        }
      }
      return total;
    } else {
      // Not viewing current month -> compute week 1..7 ? As a fallback compute last 7 days in this month (days 25-31)
      let total = 0;
      // eslint-disable-next-line no-unused-vars
      const last7 = [];
      for (let d = 25; d <= 31; d++) {
        const raw = dailyRecords[String(d)];
        const num = parseFloat(raw);
        if (!isNaN(num)) total += num;
      }
      return total;
    }
  }, [dailyRecords, selectedMonthIndex, selectedYear]);

  const handleRefresh = async () => {
    if (!monthPath || !db) return;
    setLoading(true);
    try {
      const snap = await dbGet(dbRef(db, monthPath));
      setDailyRecords(snap.val() || {});
    } catch (err) {
      console.error("Refresh error:", err);
      toast.error("Failed to refresh records");
    } finally {
      setLoading(false);
    }
  };

  const yearOptions = useMemo(() => {
    const thisYear = new Date().getFullYear();
    // show range currentYear-2 .. currentYear+1
    return [thisYear - 2, thisYear - 1, thisYear, thisYear + 1];
  }, []);

  return (
    <FarmerLayout>
    <div className="p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-green-700">Monthly Milk Records</h1>
            {/* <p className="text-gray-600">
              View liters collected per day. Path: <span className="font-mono text-xs">{monthPath || "not available"}</span>
            </p> */}
            <p className="text-sm text-gray-500 mt-1">
              Farmer: <span className="font-medium">{farmerFullName|| "—"}</span> • Company: <span className="font-medium">{companyName || "—"}</span>
            </p>
          </div>

          <div className="flex items-center gap-3">
            <select
              className="border rounded px-3 py-2"
              value={selectedMonthIndex}
              onChange={(e) => setSelectedMonthIndex(Number(e.target.value))}
            >
              {MONTHS.map((m, i) => (
                <option key={m} value={i}>
                  {m}
                </option>
              ))}
            </select>

            <select
              className="border rounded px-3 py-2"
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>

            <button
              onClick={handleRefresh}
              className="px-3 py-2 bg-cyan-600 text-white rounded"
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Left column: summary */}
          <div className="md:col-span-1 bg-white rounded-lg p-4 shadow">
            <h3 className="font-semibold text-gray-700 mb-3">Summary</h3>
            <div className="space-y-2 text-sm text-gray-700">
              <div className="flex justify-between">
                <span>First half (1–15)</span>
                <span className="font-medium">{totals.firstHalf.toFixed(1)} L</span>
              </div>
              <div className="flex justify-between">
                <span>Second half (16–31)</span>
                <span className="font-medium">{totals.secondHalf.toFixed(1)} L</span>
              </div>
              <div className="flex justify-between">
                <span>Monthly total</span>
                <span className="font-medium">{totals.monthly.toFixed(1)} L</span>
              </div>
              <div className="flex justify-between pt-2 border-t mt-2">
                <span>Current week</span>
                <span className="font-medium">{currentWeekTotal.toFixed(1)} L</span>
              </div>
            </div>
          </div>

          {/* Right: daily grid */}
          <div className="md:col-span-3 bg-white rounded-lg p-4 shadow">
            <h3 className="font-semibold text-gray-700 mb-3">Daily (1–31)</h3>

            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading month...</div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-3">
                {Array.from({ length: 31 }, (_, i) => {
                  const day = i + 1;
                  const raw = dailyRecords[String(day)];
                  return <DayCell key={day} day={day} value={raw} />;
                })}
              </div>
            )}

            <div className="mt-4 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                Showing {MONTHS[selectedMonthIndex]} {selectedYear}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    // quick nav to previous month
                    if (selectedMonthIndex === 0) {
                      setSelectedMonthIndex(11);
                      setSelectedYear((y) => y - 1);
                    } else {
                      setSelectedMonthIndex((m) => m - 1);
                    }
                  }}
                  className="px-3 py-1 border rounded"
                >
                  Prev
                </button>
                <button
                  onClick={() => {
                    if (selectedMonthIndex === 11) {
                      setSelectedMonthIndex(0);
                      setSelectedYear((y) => y + 1);
                    } else {
                      setSelectedMonthIndex((m) => m + 1);
                    }
                  }}
                  className="px-3 py-1 border rounded"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* placeholder: detailed table / export */}
        <div className="mt-6 bg-white p-4 rounded shadow-sm">
          <h4 className="font-medium text-gray-700 mb-2">Actions</h4>
          <div className="flex gap-2">
            <button
              onClick={() => {
                // quick CSV export of month
                const rows = [["Day", "Liters"]];
                for (let d = 1; d <= 31; d++) {
                  rows.push([d, dailyRecords[String(d)] || ""]);
                }
                const csvContent = rows.map((r) => r.join(",")).join("\n");
                const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `milk_${farmerOtherName || "farmer"}_${selectedYear}_${selectedMonthIndex + 1}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="px-3 py-2 bg-green-600 text-white rounded"
            >
              Export CSV
            </button>

            <button
              onClick={() => toast("This will power advanced analytics later")}
              className="px-3 py-2 border rounded"
            >
              Analyze (soon)
            </button>
          </div>
        </div>
      </div>
    </div>
    </FarmerLayout>
  );
};

export default FarmerMonthlyRecords;
